import React, { useState } from "react";
import { Gauge, Building2 } from "lucide-react";
import OpsTopBar from "../../components/ops/OpsTopBar";
import useApi from "../../hooks/useApi";

export default function OpsCapacity() {
  const { data } = useApi("/ops/capacity");
  const [extra, setExtra] = useState(0);
  if (!data) return (<><OpsTopBar title="Capacity" /><div className="p-8 text-ink-2">Loading…</div></>);

  const stats = [
    ["Managed branches", data.managed_branches],
    ["Managed actions / month", data.managed_actions_month.toLocaleString()],
    ["Operators", data.operators],
    ["Actions / operator", data.actions_per_operator.toLocaleString()],
    ["Avg actions / day / operator", data.avg_actions_day_operator],
    ["Avg task duration", data.avg_task_duration],
  ];

  // Simple model: extra branches -> extra monthly actions -> operators needed.
  const perBranch = data.managed_actions_month / data.managed_branches;
  const newBranches = data.managed_branches + extra;
  const newActions = Math.round(newBranches * perBranch);
  const opsNeeded = Math.ceil(newActions / data.actions_per_operator);
  const newUtil = Math.round((newActions / (opsNeeded * data.actions_per_operator)) * 100);

  return (
    <>
      <OpsTopBar title="Capacity" subtitle="Operations planning" />
      <div className="p-6 sm:p-8 max-w-[1100px] space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {stats.map(([l, v]) => (
            <div key={l} className="card p-5" data-testid={`capacity-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Gauge size={16} color="#5F8213" strokeWidth={1.75} />
              <div className="text-3xl font-extrabold tracking-tighter text-ink mt-2">{v}</div>
              <div className="text-xs text-ink-2 mt-0.5">{l}</div>
            </div>
          ))}
        </div>

        <div className="card p-5">
          <span className="label-caps">Estimated utilisation</span>
          <div className="flex items-center gap-4 mt-2">
            <div className="text-4xl font-extrabold tracking-tighter text-ink">{data.utilisation}%</div>
            <div className="flex-1 h-3 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
              <div className="h-full rounded-full" style={{ width: `${data.utilisation}%`, background: "var(--grad-green)" }} />
            </div>
          </div>
        </div>

        {/* What-if modeller */}
        <div className="card p-6" style={{ background: "var(--grad-ink)" }} data-testid="capacity-modeller">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#B6E14F" }}>Scenario modeller</span>
          <h3 className="text-xl font-bold tracking-tight text-white mt-2">What if we add more managed branches?</h3>
          <div className="flex items-center gap-4 mt-5">
            <Building2 size={18} color="#B6E14F" />
            <input type="range" min="0" max="500" step="50" value={extra} onChange={(e) => setExtra(+e.target.value)} data-testid="capacity-slider" className="flex-1 accent-[#93C020]" />
            <span className="text-white font-bold w-28 text-right">+{extra} branches</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            {[
              ["Total branches", newBranches],
              ["Monthly actions", newActions.toLocaleString()],
              ["Operators needed", opsNeeded],
              ["Utilisation", `${newUtil}%`],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl px-4 py-3" style={{ background: "rgba(255,255,255,.06)" }}>
                <div className="text-2xl font-extrabold tracking-tighter text-white">{v}</div>
                <div className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,.55)" }}>{l}</div>
              </div>
            ))}
          </div>
          {opsNeeded > data.operators && <p className="text-sm mt-4" style={{ color: "#B6E14F" }}>→ Hire {opsNeeded - data.operators} more operator(s) to absorb this growth.</p>}
        </div>
      </div>
    </>
  );
}
